import { SmartTable, SmartTableColumn } from '@toggle/design-system';
import { AssetClassCode, InsightsChartType } from '@toggle/toggle';
import { format } from 'date-fns';
import upperCase from 'lodash/upperCase';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  getInsightEpisodeDates,
  getInsightTotalTradingDays,
} from '~/api/insights/utils/insight-utils';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { InsightPastEpisodesData } from '~/stores/use-chat/use-chat.types';
import { formatAssetPrice } from '~/utils/currency/currency';

import * as S from '../../PastEpisodesWidget.styles';

type EpisodeTableRow = {
  key: string;
  episodeNo: number;
  date: string;
  tradingDays: number;
};

export const InsightPastEpisodes = ({
  insight,
  entity,
  hasHeader = true,
}: InsightPastEpisodesData) => {
  const { t } = useTranslation('widget');

  const episodes = insight.episodes;
  const totalTradingDays = getInsightTotalTradingDays(insight);
  const isFixedIncome = entity.asset_class === AssetClassCode.Fi;

  const chartUrl = insight.charts.find(
    chart => chart.type === InsightsChartType.ScenarioConditions
  )?.url;
  const { last_value: lastValue, last_value_date: lastValueDate } =
    insight.metadata;

  const pastEpisodesMapped: EpisodeTableRow[] = episodes.map(
    (episode, index) => ({
      key: `insight-episode-${index}`,
      episodeNo: index + 1,
      date: format(new Date(episode.start_date), 'yyyy-MM-dd'),
      tradingDays: episode.events.length,
    })
  );

  const columns: SmartTableColumn<EpisodeTableRow>[] = [
    {
      label: t('widget:pastEpisode.table.episodeNo'),
      key: 'episodeNo',
    },
    {
      label: t('widget:pastEpisode.table.episodeDate'),
      key: 'date',
      render: value => <S.NumericalValue>{value}</S.NumericalValue>,
    },
    {
      label: t('widget:pastEpisode.table.tradingDays', {
        totalTradingDays: totalTradingDays,
      }),
      key: 'tradingDays',
      alignment: 'right',
    },
  ];

  return (
    <>
      <S.Title data-testid="episode-description">
        <span>
          <span>{t('widget:pastEpisode:title.occurrences')}</span>
          <TooltipContainer
            label={t('analyze:experiment.chart.tooltip')}
            showUnderline
          >
            <span>
              {t('widget:pastEpisode:title.episodes', {
                episodes: getInsightEpisodeDates(episodes).length,
              })}
            </span>
          </TooltipContainer>
          <span>
            {t('widget:pastEpisode:title.days', {
              totalTradingDays,
            })}
          </span>
        </span>
      </S.Title>
      {chartUrl && (
        <>
          <S.PastEpisodeText>
            <S.PastEpisodeDetails>
              <span>
                <S.PastEpisodeTickerLabel>
                  {t('widget:pastEpisode.title', {
                    entityName: entity.name,
                    currency: upperCase(entity.currency),
                    context: isFixedIncome ? 'yield' : 'price',
                  })}
                </S.PastEpisodeTickerLabel>
                <S.NumericalValue>
                  {formatAssetPrice(lastValue)}
                </S.NumericalValue>
                <S.Subtext>
                  {t('widget:pastEpisode.asOf', {
                    date: format(
                      new Date(lastValueDate ?? insight.created_at),
                      'MMM dd, yyyy'
                    ),
                  })}
                </S.Subtext>
              </span>
            </S.PastEpisodeDetails>
            <S.PastEpisodeDetails>
              <S.PastEpisodeKey />
              {t('widget:pastEpisode.pastEpisode')}
            </S.PastEpisodeDetails>
          </S.PastEpisodeText>
          <S.ChartImage
            src={chartUrl}
            alt={t('widget:pastEpisode.chartAlt')}
            data-testid="past-episodes-chart"
          />
        </>
      )}

      {hasHeader && <S.Title>{t('widget:pastEpisode.pastEpisode')}</S.Title>}
      <S.TableRoot>
        <SmartTable
          columns={columns}
          data={pastEpisodesMapped}
          withBodyRowHover={false}
        />
      </S.TableRoot>
    </>
  );
};
